import apiClient from './apiClient';

export type PreAuthStatus = 'pending' | 'approved' | 'rejected' | 'expired' | 'cancelled' | 'used';
export type PreAuthType = 'procedure' | 'lab' | 'imaging' | 'medication' | 'hospitalization' | 'consultation';
export type PreAuthServiceType = 'service' | 'lab_test' | 'imaging_study' | 'medication' | 'other';

export interface PreAuthorization {
  id: number;
  patient_id: number;
  patient_insurance_id: number;
  authorization_number: string;
  authorization_type: PreAuthType;
  service_type: PreAuthServiceType;
  service_id: number | null;
  service_name: string;
  quantity: number;
  requested_by: number;
  status: PreAuthStatus;
  request_date: string;
  authorization_date: string | null;
  expiration_date: string | null;
  approved_amount: string | null;
  copay_amount: string | null;
  deductible_amount: string | null;
  coverage_percentage: number | null;
  notes: string | null;
  rejection_reason: string | null;
  authorized_by: number | null;
  external_auth_code: string | null;
  patient?: { id: number; first_name: string; last_name: string };
  patientInsurance?: {
    id: number;
    policy_number: string;
    company?: { id: number; name: string };
    plan?: { id: number; name: string };
  };
  requestedBy?: { id: number; first_name: string; last_name: string };
  authorizedBy?: { id: number; name: string };
}

export interface CreatePreAuthRequest {
  patient_id: number;
  patient_insurance_id: number;
  authorization_type: PreAuthType;
  service_type: PreAuthServiceType;
  service_id?: number;
  service_name: string;
  quantity?: number;
  requested_by: number;
  notes?: string;
}

export interface ApprovePreAuthRequest {
  approved_amount?: number;
  copay_amount?: number;
  deductible_amount?: number;
  coverage_percentage?: number;
  expiration_date?: string;
  notes?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  per_page: number;
  current_page: number;
  last_page?: number;
}

type RawPreAuthorization = PreAuthorization & {
  patient_insurance?: PreAuthorization['patientInsurance'];
  requested_by?: number | { id: number; first_name: string; last_name: string };
};

function isRequestedByDoctor(
  value: RawPreAuthorization['requested_by'],
): value is { id: number; first_name: string; last_name: string } {
  return typeof value === 'object' && value !== null && 'id' in value;
}

function normalizePreAuthorization(raw: RawPreAuthorization): PreAuthorization {
  const requestedByObj = isRequestedByDoctor(raw.requested_by) ? raw.requested_by : raw.requestedBy;
  const requestedById = isRequestedByDoctor(raw.requested_by)
    ? raw.requested_by.id
    : typeof raw.requested_by === 'number'
      ? raw.requested_by
      : requestedByObj?.id ?? 0;

  return {
    ...raw,
    patientInsurance: raw.patientInsurance ?? raw.patient_insurance,
    requestedBy: requestedByObj,
    requested_by: requestedById,
  };
}

export const preAuthorizationService = {
  async getAll(params: {
    page?: number;
    per_page?: number;
    status?: PreAuthStatus;
    patient_id?: number;
    authorization_type?: PreAuthType;
    service_type?: PreAuthServiceType;
    search?: string;
  } = {}): Promise<PaginatedResponse<PreAuthorization>> {
    const q = new URLSearchParams();
    if (params.page) q.append('page', String(params.page));
    if (params.per_page) q.append('per_page', String(params.per_page));
    if (params.status) q.append('status', params.status);
    if (params.patient_id) q.append('patient_id', String(params.patient_id));
    if (params.authorization_type) q.append('authorization_type', params.authorization_type);
    if (params.service_type) q.append('service_type', params.service_type);
    if (params.search) q.append('search', params.search);
    const res = await apiClient.get<PaginatedResponse<RawPreAuthorization>>(`/pre-authorizations?${q.toString()}`);
    return { ...res.data, data: (res.data.data ?? []).map(normalizePreAuthorization) };
  },

  async getById(id: number): Promise<PreAuthorization> {
    const res = await apiClient.get<ApiResponse<RawPreAuthorization>>(`/pre-authorizations/${id}`);
    return normalizePreAuthorization(res.data.data);
  },

  async getByPatient(patientId: number): Promise<PreAuthorization[]> {
    const res = await apiClient.get<ApiResponse<RawPreAuthorization[]>>(`/pre-authorizations/patient/${patientId}`);
    return (res.data.data ?? []).map(normalizePreAuthorization);
  },

  async getActiveByPatient(patientId: number): Promise<PreAuthorization[]> {
    const all = await preAuthorizationService.getByPatient(patientId);
    const today = new Date().toISOString().split('T')[0];
    return all.filter(
      (p) =>
        p.status === 'approved' &&
        (p.expiration_date === null || p.expiration_date >= today)
    );
  },

  async getPending(): Promise<PreAuthorization[]> {
    const res = await apiClient.get<ApiResponse<RawPreAuthorization[]>>('/pre-authorizations/pending');
    return (res.data.data ?? []).map(normalizePreAuthorization);
  },

  async create(data: CreatePreAuthRequest): Promise<PreAuthorization> {
    const res = await apiClient.post<ApiResponse<RawPreAuthorization>>('/pre-authorizations', data);
    return normalizePreAuthorization(res.data.data);
  },

  async approve(id: number, data: ApprovePreAuthRequest): Promise<PreAuthorization> {
    const res = await apiClient.put<ApiResponse<RawPreAuthorization>>(`/pre-authorizations/${id}/approve`, data);
    return normalizePreAuthorization(res.data.data);
  },

  async reject(id: number, rejectionReason: string, notes?: string): Promise<PreAuthorization> {
    const res = await apiClient.put<ApiResponse<RawPreAuthorization>>(`/pre-authorizations/${id}/reject`, {
      rejection_reason: rejectionReason,
      notes,
    });
    return normalizePreAuthorization(res.data.data);
  },

  async cancel(id: number): Promise<PreAuthorization> {
    const res = await apiClient.put<ApiResponse<RawPreAuthorization>>(`/pre-authorizations/${id}/cancel`, {});
    return normalizePreAuthorization(res.data.data);
  },

  async markAsUsed(id: number): Promise<PreAuthorization> {
    const res = await apiClient.put<ApiResponse<RawPreAuthorization>>(`/pre-authorizations/${id}/mark-used`, {});
    return normalizePreAuthorization(res.data.data);
  },

  async updateExternalCode(id: number, code: string | null): Promise<PreAuthorization> {
    const res = await apiClient.patch<ApiResponse<RawPreAuthorization>>(`/pre-authorizations/${id}/external-code`, {
      external_auth_code: code || null,
    });
    return normalizePreAuthorization(res.data.data);
  },
};

export default preAuthorizationService;
